import { Link, useLocation } from 'react-router-dom'
import {
  HomeIcon,
  DocumentTextIcon,
  UserGroupIcon,
  UsersIcon,
  ClockIcon,
  EnvelopeIcon,
  CogIcon,
  DocumentDuplicateIcon,
  XMarkIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline'
import { useAuthStore } from '../../stores/authStore'
import { useUIStore } from '../../stores/uiStore'

/**
 * Sidebar Component
 * Main navigation sidebar with red accent for active items
 */
export default function Sidebar() {
  const location = useLocation()
  const { user } = useAuthStore()
  const { sidebarOpen, setSidebarOpen } = useUIStore()
  
  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Contracts', href: '/contracts', icon: DocumentTextIcon },
    { name: 'Templates', href: '/templates', icon: DocumentDuplicateIcon, adminOnly: true },
    { name: 'Trainers', href: '/trainers', icon: UsersIcon },
    { name: 'Teams', href: '/teams', icon: UserGroupIcon },
    { name: 'Payments', href: '/payments', icon: BanknotesIcon, adminOnly: true },
    { name: 'Activity Logs', href: '/activity', icon: ClockIcon, adminOnly: true },
    { name: 'Email History', href: '/emails', icon: EnvelopeIcon, adminOnly: true },
    { name: 'Settings', href: '/settings', icon: CogIcon, adminOnly: true },
  ]
  
  const isActive = (href) => {
    return location.pathname.startsWith(href)
  }

  const handleLinkClick = () => {
    // Close sidebar on mobile when a link is clicked
    if (window.innerWidth < 768) {
      setSidebarOpen(false)
    }
  }
  
  return (
    <>
      {/* Mobile sidebar - sliding overlay */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          md:hidden
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex-1 flex flex-col h-full pt-5 pb-4 overflow-y-auto">
          {/* Logo and close button */}
          <div className="flex items-center justify-between flex-shrink-0 px-4 mb-8">
            <h1 className="text-2xl font-bold text-primary-600">
              Contract Manager
            </h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close menu"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-2 space-y-1">
            {navigation.map((item) => {
              // Skip admin-only items if user is not admin
              if (item.adminOnly && user?.role !== 'admin') return null;
              
              const active = isActive(item.href)
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={handleLinkClick}
                  className={`
                    group flex items-center px-2 py-2 text-sm font-medium rounded-lg transition-colors
                    ${active
                      ? 'bg-primary-50 text-primary-600 border-l-4 border-primary-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                >
                  <item.icon
                    className={`mr-3 flex-shrink-0 h-6 w-6 ${
                      active ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  />
                  {item.name}
                </Link>
              )
            })}
          </nav>
        </div>
      </aside>

      {/* Desktop sidebar - always visible */}
      <aside className="hidden md:flex md:flex-col md:w-64 bg-white border-r border-gray-200">
        <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0 px-4">
            <h1 className="text-2xl font-bold text-primary-600">
              Contract Manager
            </h1>
          </div>
          
          {/* Navigation */}
          <nav className="mt-8 flex-1 px-2 space-y-1">
            {navigation.map((item) => {
              // Skip admin-only items if user is not admin
              if (item.adminOnly && user?.role !== 'admin') return null;
              
              const active = isActive(item.href)
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    group flex items-center px-2 py-2 text-sm font-medium rounded-lg transition-colors
                    ${active
                      ? 'bg-primary-50 text-primary-600 border-l-4 border-primary-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                >
                  <item.icon
                    className={`mr-3 flex-shrink-0 h-6 w-6 ${
                      active ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  />
                  {item.name}
                </Link>
              )
            })}
          </nav>
        </div>
      </aside>
    </>
  )
}